import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FilterMatchMode } from "primereact/api";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column, type ColumnFilterElementTemplateOptions } from "primereact/column";
import {
  DataTable,
  type DataTableFilterMeta,
  type DataTablePageEvent,
  type DataTableRowClickEvent,
  type DataTableStateEvent,
} from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Sidebar } from "primereact/sidebar";
import { Dropdown } from "primereact/dropdown";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { ConnectError, getAuditClient } from "sj-ui-toolkit/src/client";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { isUuid } from "sj-ui-toolkit/src/utils/string";
import { useToast } from "sj-ui-toolkit/src/hooks";
import type {
  AuditEvent,
  AuditPersonRole,
  AuditTaxonomy,
  ListEventsOptions,
} from "sj-ui-toolkit/src/client/svcAudit";

const PAGE_SIZE = 100;
const GRPC_INVALID_ARGUMENT = 3;
interface AuditLogFilters {
  action: string;
  category: string;
  severity: string;
  occurredAtStart: Date | null;
  occurredAtEnd: Date | null;
}

// Column filters on Actor / Target. The API takes one person per request, so only one of
// the two can be applied at a time
interface PersonFilter {
  person: string;
  role: AuditPersonRole;
}

interface TaxonomyOption {
  label: string;
  value: string;
}

const EMPTY_FILTERS: AuditLogFilters = {
  action: "",
  category: "",
  severity: "",
  occurredAtStart: null,
  occurredAtEnd: null,
};

const formatDate = (value: string | undefined): string => {
  if (!value) return "--";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatValue = (value: unknown): string =>
  typeof value === "string" ? value : JSON.stringify(value ?? null);

const formatServiceName = (service: string | undefined): string => {
  if (!service) return "Unknown service";

  const name = service.replace(/^svc-/, "");
  return `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
};

const formatActor = (event: AuditEvent): string => {
  if (event.actorType === "service") {
    return `service: ${formatServiceName(event.actorService)}`;
  }

  const identity =
    event.actorEmail ??
    event.actorDirectoryUuid ??
    event.apiTokenId ??
    event.aiAgent ??
    event.actorService ??
    "Unknown";
  const impersonation = event.impersonatedDirectoryUuid
    ? `, impersonating ${event.impersonatedDirectoryUuid}`
    : "";

  return `${event.actorType ?? "Unknown"}: ${identity}${impersonation}`;
};

const compareOccurredAtDescending = (left: AuditEvent, right: AuditEvent): number => {
  const leftTime = Date.parse(left.occurredAt ?? "");
  const rightTime = Date.parse(right.occurredAt ?? "");
  return (Number.isNaN(rightTime) ? 0 : rightTime) - (Number.isNaN(leftTime) ? 0 : leftTime);
};

interface ActorDetail {
  label: string;
  value: string | undefined;
}

const actorDetails = (event: AuditEvent): ActorDetail[] => {
  switch (event.actorType) {
    case "user":
      return [
        { label: "Email", value: event.actorEmail },
        { label: "Directory UUID", value: event.actorDirectoryUuid },
        { label: "Impersonated UUID", value: event.impersonatedDirectoryUuid },
      ];
    case "api_token":
      return [{ label: "API token ID", value: event.apiTokenId }];
    case "service":
      return [{ label: "Service", value: formatServiceName(event.actorService) }];
    case "ai_agent":
      return [
        { label: "Agent", value: event.aiAgent },
        { label: "Email", value: event.actorEmail },
        { label: "Directory UUID", value: event.actorDirectoryUuid },
        { label: "API token ID", value: event.apiTokenId },
        { label: "Auth source", value: event.authSource },
        { label: "OAuth client ID", value: event.oauthClientId },
        {
          label: "Service",
          value: event.actorService ? formatServiceName(event.actorService) : undefined,
        },
      ];
    default:
      return [];
  }
};

function DetailField({ label, value }: { label: string; value: unknown }) {
  const displayValue = Array.isArray(value) ? value.join(", ") || "--" : formatValue(value);

  return (
    <div className="min-w-0">
      <dt className="text-sm font-semibold text-secondary">{label}</dt>
      <dd className="mt-1 break-words text-sm">{displayValue}</dd>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  const sectionId = `audit-detail-${title.toLowerCase().replaceAll(" ", "-")}`;

  return (
    <section aria-labelledby={sectionId}>
      <h2 id={sectionId} className="atlas-h3 mb-3 border-b pb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

// The API takes exactly one of email / directoryUuid plus the person's role
const personQuery = (
  filter: PersonFilter | null
): Pick<ListEventsOptions, "email" | "directoryUuid" | "personRole"> => {
  if (!filter) return {};
  return {
    ...(isUuid(filter.person) ? { directoryUuid: filter.person } : { email: filter.person }),
    personRole: filter.role,
  };
};

const toQuery = (
  filters: AuditLogFilters,
  personFilter: PersonFilter | null
): ListEventsOptions => ({
  pageSize: PAGE_SIZE,
  action: filters.action || undefined,
  category: filters.category || undefined,
  severity: filters.severity || undefined,
  ...personQuery(personFilter),
  occurredAtStart: filters.occurredAtStart?.toISOString(),
  occurredAtEnd: filters.occurredAtEnd?.toISOString(),
});

const filterText = (meta: DataTableFilterMeta[string] | undefined): string =>
  meta && "value" in meta && typeof meta.value === "string" ? meta.value.trim() : "";

const ACTOR_FILTER_HINT =
  "Took the action, directly or while impersonated. " +
  "One person per search: applying this clears the Target filter.";
const TARGET_FILTER_HINT =
  "Their data was part of the action, as the target user or a user referenced in the details. " +
  "One person per search: applying this clears the Actor filter.";

interface PersonFilterInputProps {
  id: string;
  label: string;
  hint: string;
  options: ColumnFilterElementTemplateOptions;
}

function PersonFilterInput({ id, label, hint, options }: PersonFilterInputProps) {
  return (
    <div className="flex flex-col gap-2 pt-[1.125rem]">
      <InputText
        id={id}
        value={options.value ?? ""}
        placeholder="Email starts with, or directory UUID"
        aria-label={label}
        aria-describedby={`${id}-hint`}
        onChange={event => options.filterCallback(event.target.value)}
      />
      <p id={`${id}-hint`} className="body-sm text-secondary mb-0">
        {hint}
      </p>
    </div>
  );
}

const taxonomyOptions = (values: AuditTaxonomy["categories"]): TaxonomyOption[] =>
  (values ?? []).flatMap(value =>
    value.value ? [{ label: value.displayName ?? value.value, value: value.value }] : []
  );

export default function AuditLog() {
  useBreadcrumb({
    label: "Audit Log",
    route: AdminAppRoutes.routes.auditLog.route,
    display: "none",
    resetTrail: true,
  });

  const { showToast } = useToast();
  const tenant = getCurrentSelectedTenant() ?? "";
  const pageTokens = useRef<Record<number, string | undefined>>({ 0: undefined });
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [filters, setFilters] = useState<AuditLogFilters>(EMPTY_FILTERS);
  const [personFilter, setPersonFilter] = useState<PersonFilter | null>(null);
  const [draftFilters, setDraftFilters] = useState<AuditLogFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [taxonomy, setTaxonomy] = useState<AuditTaxonomy | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);
  const [showChangeDetails, setShowChangeDetails] = useState(false);

  const loadPage = useCallback(
    async (pageNumber: number, nextFilters: AuditLogFilters, nextPerson: PersonFilter | null) => {
      if (!tenant) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await getAuditClient().listEvents(tenant, {
          ...toQuery(nextFilters, nextPerson),
          pageToken: pageTokens.current[pageNumber],
        });
        if (error) throw error;
        setEvents([...(data?.events ?? [])].sort(compareOccurredAtDescending));
        setNextPageToken(data?.nextPageToken);
        if (data?.nextPageToken) pageTokens.current[pageNumber + 1] = data.nextPageToken;
      } catch (error) {
        console.error("Failed to load audit log:", error);
        setEvents([]);
        setNextPageToken(undefined);
        // INVALID_ARGUMENT carries an actionable message, anything else is not the user's to fix
        const detail =
          error instanceof ConnectError && error.code === GRPC_INVALID_ARGUMENT
            ? error.message
            : "Failed to load audit log";
        showToast({ severity: "error", detail });
      } finally {
        setLoading(false);
      }
    },
    [showToast, tenant]
  );

  useEffect(() => {
    void loadPage(page, filters, personFilter);
  }, [filters, loadPage, page, personFilter]);

  // PrimeReact owns the draft while a filter menu is open; the applied value is personFilter
  const appliedActor = personFilter?.role === "PERSON_ROLE_ACTOR" ? personFilter.person : "";
  const appliedTarget = personFilter?.role === "PERSON_ROLE_SUBJECT" ? personFilter.person : "";
  const tableFilters = useMemo<DataTableFilterMeta>(
    () => ({
      actor: { value: appliedActor || null, matchMode: FilterMatchMode.CUSTOM },
      target: { value: appliedTarget || null, matchMode: FilterMatchMode.CUSTOM },
    }),
    [appliedActor, appliedTarget]
  );

  useEffect(() => {
    const loadTaxonomy = async () => {
      try {
        const { data, error } = await getAuditClient().getTaxonomy();
        if (error) throw error;
        setTaxonomy(data);
      } catch (error) {
        console.error("Failed to load audit taxonomy:", error);
        showToast({ severity: "error", detail: "Failed to load audit filter options" });
      }
    };

    void loadTaxonomy();
  }, [showToast]);

  const applyFilters = () => {
    pageTokens.current = { 0: undefined };
    setPage(0);
    setFilters({ ...draftFilters });
  };

  const clearFilters = () => {
    pageTokens.current = { 0: undefined };
    setPage(0);
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPersonFilter(null);
  };

  // Only one person filter can be active, so whichever column changed wins
  const handleFilter = (event: DataTableStateEvent) => {
    const actor = filterText(event.filters.actor);
    const target = filterText(event.filters.target);
    const role = actor !== appliedActor ? "PERSON_ROLE_ACTOR" : "PERSON_ROLE_SUBJECT";
    const person = role === "PERSON_ROLE_ACTOR" ? actor : target;
    if (person === (role === "PERSON_ROLE_ACTOR" ? appliedActor : appliedTarget)) return;
    pageTokens.current = { 0: undefined };
    setPage(0);
    setPersonFilter(person ? { person, role } : null);
  };

  const handlePage = (event: DataTablePageEvent) => {
    if (event.page === undefined || (event.page > page && !nextPageToken)) return;
    setPage(event.page);
  };

  const selectEvent = (event: AuditEvent) => {
    setSelectedEvent(event);
    setShowChangeDetails(false);
  };

  const totalRecords = nextPageToken ? (page + 2) * PAGE_SIZE : page * PAGE_SIZE + events.length;

  return (
    <div data-testid="admin-audit-log-page" aria-labelledby="audit-log-heading">
      <h1 id="audit-log-heading" className="atlas-h1 mb-4">
        Audit Log
      </h1>

      <Card>
        <section aria-labelledby="audit-log-filters-heading" className="mb-5">
          <h2 id="audit-log-filters-heading" className="atlas-h3 mb-3">
            Filters
          </h2>
          <div className="flex flex-wrap items-end gap-3">
            <span className="flex flex-col gap-1">
              <label htmlFor="audit-action">Action</label>
              <InputText
                id="audit-action"
                value={draftFilters.action}
                onChange={event => setDraftFilters({ ...draftFilters, action: event.target.value })}
              />
            </span>
            <span className="flex flex-col gap-1">
              <label htmlFor="audit-category">Category</label>
              <Dropdown
                id="audit-category"
                value={draftFilters.category}
                options={taxonomyOptions(taxonomy?.categories)}
                optionLabel="label"
                optionValue="value"
                placeholder="All categories"
                showClear
                disabled={taxonomy === null}
                onChange={event =>
                  setDraftFilters({ ...draftFilters, category: event.value ?? "" })
                }
              />
            </span>
            <span className="flex flex-col gap-1">
              <label htmlFor="audit-severity">Severity</label>
              <Dropdown
                id="audit-severity"
                value={draftFilters.severity}
                options={taxonomyOptions(taxonomy?.severities)}
                optionLabel="label"
                optionValue="value"
                placeholder="All severities"
                showClear
                disabled={taxonomy === null}
                onChange={event =>
                  setDraftFilters({ ...draftFilters, severity: event.value ?? "" })
                }
              />
            </span>
            <span className="flex flex-col gap-1">
              <label htmlFor="audit-date-range">Occurred between</label>
              <Calendar
                id="audit-date-range"
                value={
                  draftFilters.occurredAtStart
                    ? [draftFilters.occurredAtStart, draftFilters.occurredAtEnd]
                    : null
                }
                onChange={event => {
                  const range = event.value as Date[] | null;
                  setDraftFilters({
                    ...draftFilters,
                    occurredAtStart: range?.[0] ?? null,
                    occurredAtEnd: range?.[1] ?? null,
                  });
                }}
                selectionMode="range"
                readOnlyInput
                showIcon
                inputId="audit-date-range"
              />
            </span>
            <Button label="Apply filters" icon="pi pi-filter" onClick={applyFilters} />
            <Button
              label="Clear"
              icon="pi pi-filter-slash"
              severity="secondary"
              onClick={clearFilters}
            />
          </div>
        </section>

        {!tenant && <Message severity="warn" text="Select a tenant to view its audit log." />}
        {tenant && loading && (
          <div className="flex justify-center p-6" role="status" aria-label="Loading audit log">
            <ProgressSpinner aria-label="Loading audit log" />
          </div>
        )}
        {tenant && !loading && (
          <DataTable
            value={events}
            lazy
            paginator
            first={page * PAGE_SIZE}
            rows={PAGE_SIZE}
            totalRecords={totalRecords}
            onPage={handlePage}
            filters={tableFilters}
            onFilter={handleFilter}
            filterDisplay="menu"
            emptyMessage="No audit events found."
            selectionMode="single"
            onRowClick={(event: DataTableRowClickEvent) => selectEvent(event.data as AuditEvent)}
            rowHover
            dataKey="eventId"
          >
            <Column
              field="occurredAt"
              header="Occurred"
              body={(event: AuditEvent) => formatDate(event.occurredAt)}
            />
            <Column field="category" header="Category" />
            <Column field="action" header="Action" />
            <Column field="severity" header="Severity" />
            <Column
              field="actorType"
              header="Actor"
              body={(event: AuditEvent) => formatActor(event)}
              filter
              filterField="actor"
              filterElement={options => (
                <PersonFilterInput
                  id="audit-actor-filter"
                  label="Actor"
                  hint={ACTOR_FILTER_HINT}
                  options={options}
                />
              )}
              showFilterMatchModes={false}
              filterMenuStyle={{ width: "22rem" }}
            />
            <Column
              field="targetDisplayName"
              header="Target"
              body={(event: AuditEvent) =>
                event.targetDisplayName || `${event.targetType}:${event.targetId}`
              }
              filter
              filterField="target"
              filterElement={options => (
                <PersonFilterInput
                  id="audit-target-filter"
                  label="Target"
                  hint={TARGET_FILTER_HINT}
                  options={options}
                />
              )}
              showFilterMatchModes={false}
              filterMenuStyle={{ width: "22rem" }}
            />
          </DataTable>
        )}
      </Card>

      <Sidebar
        header="Audit event details"
        visible={selectedEvent !== null}
        onHide={() => setSelectedEvent(null)}
        position="right"
        className="w-full md:w-[42rem]"
        aria-label="Audit event details"
      >
        {selectedEvent && (
          <div className="flex flex-col gap-6" id="audit-event-details">
            <DetailSection title="Event">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailField label="Event ID" value={selectedEvent.eventId} />
                <DetailField label="Tenant" value={selectedEvent.tenant} />
                <DetailField label="Occurred" value={formatDate(selectedEvent.occurredAt)} />
                <DetailField label="Received" value={formatDate(selectedEvent.receivedAt)} />
                <DetailField label="Category" value={selectedEvent.category} />
                <DetailField label="Action" value={selectedEvent.action} />
                <DetailField label="Severity" value={selectedEvent.severity} />
                <DetailField label="Action type" value={selectedEvent.detailsActionType} />
              </dl>
            </DetailSection>

            <DetailSection title="Actor">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailField label="Type" value={selectedEvent.actorType} />
                {actorDetails(selectedEvent)
                  .filter(detail => detail.value !== undefined)
                  .map(detail => (
                    <DetailField key={detail.label} label={detail.label} value={detail.value} />
                  ))}
              </dl>
            </DetailSection>

            <DetailSection title="Target">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailField label="Type" value={selectedEvent.targetType} />
                <DetailField label="ID" value={selectedEvent.targetId} />
                <DetailField label="Display name" value={selectedEvent.targetDisplayName} />
              </dl>
            </DetailSection>

            {(selectedEvent.detailsBefore || selectedEvent.detailsAfter) && (
              <DetailSection title="Before and after">
                <div className="grid grid-cols-1 gap-4">
                  {selectedEvent.detailsBefore && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-secondary">Before</h3>
                      <pre className="overflow-auto rounded border p-3 text-xs whitespace-pre-wrap">
                        {formatValue(selectedEvent.detailsBefore)}
                      </pre>
                    </div>
                  )}
                  {selectedEvent.detailsAfter && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-secondary">After</h3>
                      <pre className="overflow-auto rounded border p-3 text-xs whitespace-pre-wrap">
                        {formatValue(selectedEvent.detailsAfter)}
                      </pre>
                    </div>
                  )}
                </div>
              </DetailSection>
            )}

            <section>
              <Button
                label={showChangeDetails ? "Show less" : "Show more"}
                aria-expanded={showChangeDetails}
                aria-controls="audit-change-details"
                severity="secondary"
                text
                onClick={() => setShowChangeDetails(show => !show)}
              />
              {showChangeDetails && (
                <div id="audit-change-details" className="mt-3">
                  <h2 id="audit-change-details-heading" className="atlas-h3 mb-3 border-b pb-2">
                    Change details
                  </h2>
                  <dl className="grid grid-cols-1 gap-4">
                    <DetailField label="Field set" value={selectedEvent.detailsFieldSet} />
                    <DetailField
                      label="Changed fields"
                      value={selectedEvent.detailsChangedFields}
                    />
                    <DetailField label="Affected IDs" value={selectedEvent.detailsAffectedIds} />
                    <DetailField label="User references" value={selectedEvent.detailsUserRefs} />
                    <DetailField label="Reason" value={selectedEvent.detailsReason} />
                  </dl>
                </div>
              )}
            </section>
          </div>
        )}
      </Sidebar>
    </div>
  );
}
